import React, { useEffect, useMemo, useRef, useState } from 'react';
import { graphql, useStaticQuery } from "gatsby";
import { ReactComponent as ArrowDownIcon } from "../images/icons/arrow-down.svg";

const ALL_BRANDS_OPTION = {
  label: "All Brands",
  value: "all-brands",
};

const normalizeBrandOptions = (edges = []) => {
  const uniqueOptions = new Map();

  (edges || []).forEach((edge) => {
    const node = edge?.node;
    const slug = String(node?.slug || "").trim();
    const title = String(node?.title || "").trim();

    if (!slug || !title) {
      return;
    }

    uniqueOptions.set(slug, {
      label: title,
      value: slug,
    });
  });

  return Array.from(uniqueOptions.values()).sort((leftOption, rightOption) =>
    leftOption.label.localeCompare(rightOption.label)
  );
};

const SearchDropdown = ({ value = ALL_BRANDS_OPTION.value, onChange }) => {
  const data = useStaticQuery(graphql`
    query SearchDropdownBrandOptionsQuery {
      allWpBrand {
        edges {
          node {
            slug
            title
          }
        }
      }
    }
  `);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const options = useMemo(
    () => [
      ALL_BRANDS_OPTION,
      ...normalizeBrandOptions(data?.allWpBrand?.edges),
    ],
    [data]
  );
  const selectedOption =
    options.find((option) => option.value === value) || ALL_BRANDS_OPTION;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  return (
    <div ref={dropdownRef} className="relative h-[48px]">
      {/* Dropdown Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-[8px] h-[48px] w-full bg-mqgrey2 rounded-[8px] px-[16px] hover:bg-mqgrey1 transition-colors relative"
      >
        <span 
          className="flex-1 text-left font-work-sans font-normal"
          style={{ fontSize: '16px', lineHeight: '20px', color: '#555555' }}
        >
          {selectedOption.label}
        </span>
        <ArrowDownIcon 
          className={`flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          style={{ width: '24px', height: '24px', color: '#555555' }}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-[2px] bg-white rounded-bl-[8px] rounded-br-[8px] rounded-tr-[8px] shadow-shadow-3 z-40 overflow-hidden w-full">
          <div className="flex flex-col gap-[6px] p-[7px] max-h-[300px] overflow-y-auto">
            {options.map((option) => (
              <button
                type="button"
                key={option.value}
                onClick={() => {
                  onChange?.(option.value);
                  setIsOpen(false);
                }}
                className={`w-full text-left px-[16px] py-[12px] rounded-[6px] transition-colors text-_1424 font-work-sans font-medium ${
                  selectedOption.value === option.value
                    ? 'bg-mqgrey0-5 text-mqgrey5'
                    : 'text-mqgrey5 hover:bg-mqgrey0-5'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchDropdown;
